import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { UrlSegment } from '@angular/router';

import { ApiService } from '../api.service';
import { BehaviorSubject } from 'rxjs';
import { Category } from 'lib/services/category/category.class';
import { isNestedCategoryUrl } from 'lib/services/category/category-url.util';
import { setCategoryPriceCeilings } from 'lib/services/category/category-price-ceiling.store';
import { CmsService } from 'lib/services/cms.service';
import { contains, isDevServer } from 'lib/tools';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import { RouterService } from 'lib/services/router.service';
import { SearchSpringService } from '../searchspring.service';

const scriptName = 'category.service';

const CATEGORY_CONTENT_STATE_KEY = makeStateKey<object>('categoryContent');
const CATEGORIES_DATA_STATE_KEY = makeStateKey<object>('categoriesData');
const CATEGORY_PRICE_CEILINGS_STATE_KEY = makeStateKey<Record<string, number>>('categoryPriceCeilings');
const CMS_DATA_STATE_KEY = makeStateKey<object>('cmsData');

// ST-3626: nested category_page urls refused by initRoutes. Module-level so a misauthored entry is
// reported once per process (SSR bootstraps once per request) rather than on every page render.
const reportedShadowingUrls = new Set<string>();

@Injectable()
export class CategoryService {
    private environment: any;
    private apiService: ApiService;
    private cmsService: CmsService;
    private location: Location;
    private routerService: RouterService;
    private searchSpringService: SearchSpringService;
    private transferState: TransferState;

    // Properties: private

    private categoryContentStateData: any = {
        urlParams: '',
        type: '',
        breadcrumb: '',
        res: {},
    };

    // Properties: public

    category: Category;
    isBrowser: boolean;
    retrieving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    pageTitle: string = '';
    pageTitles: string[] = [];
    paths: string[] = [];
    queryParamSubscription: any;
    transferredCategory: any;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject('environment') e: any,
        a: ApiService,
        c: CmsService,
        l: Location,
        r: RouterService,
        s: SearchSpringService,
        t: TransferState
    ) {
        try {
            this.isBrowser = isPlatformBrowser(this.platformId);
            this.apiService = a;
            this.cmsService = c;
            this.location = l;
            this.environment = e;
            this.routerService = r;
            this.searchSpringService = s;
            this.transferState = t;
            this.category = new Category({
                environment: this.environment,
                location: this.location,
                routerService: this.routerService,
                isBrowser: this.isBrowser,
            });
            this.initRouteListener();
        } catch (err) {
            console.error(...new ExxComError(520993, scriptName, err).stamp());
        }
    }

    private getUrl = () => {
        const path = `/${this.routerService.url.component}`;
        return path.replace('/category/', '');
    };

    // ROUTE INITIALIZATION

    private initRouteListener() {
        try {
            this.routerService.onRouteChange('categoryService', (event: any) => {
                if (!this.routerService.isCategoryPage(this.paths) || contains(event.url, 'reset-password')) {
                    return;
                } // Not category page
                if (contains(event.url, '/search') && (/\/search\??q?=?$/.test(event.url) || !contains(event.url, 'q='))) {
                    // Invalid search
                    this.router.navigateByUrl('/');
                } else {
                    this.initCategory();
                }
            });
        } catch (err) {
            console.error(...new ExxComError(420939, scriptName, err).stamp());
        }
    }

    /**
     * @function isReservedRoutePrefix
     * @description True when a nested category url would be claimed by one of the app's own
     * routes: its first segment equals a static path (blog, account, documents, ...) or one of
     * the app's URL matchers accepts it (products, gated content). Routes flagged with
     * `data.categoryPages` (the /Services/* matcher) serve category_page entries themselves and
     * are ignored. Runs during APP_INITIALIZER, before any category routes have been unshifted,
     * so router.config holds only the app's own routes.
     */
    private isReservedRoutePrefix(url: string): boolean {
        const segments = url.split('/').filter(Boolean);
        const first = segments[0];
        const urlSegments = segments.map((segment) => new UrlSegment(segment, {}));
        return this.router.config.some((route: any) => {
            if (get(route, 'data.categoryPages', false)) {
                return false;
            }
            if (route.path === first) {
                return true;
            }
            if (typeof route.matcher === 'function') {
                try {
                    return !!route.matcher(urlSegments, null, route);
                } catch {
                    return false;
                }
            }
            return false;
        });
    }

    /**
     * @function initRoutes
     * @param {CategoryComponent} component
     * @description Called in app.initializers ServicesInitializer, it retrieves
     * category URLs from the ExxCom database.
     */
    async initRoutes(component: any) {
        try {
            const setUrls = (exc: any = [], cms: any = []) => {
                this.paths = [];
                if (!exc || !Array.isArray(exc)) {
                    return;
                }
                exc.forEach((d: any) => this.paths.push(d.urlComponent.replace(/^\//, '')));

                // SSR-safe: Ensure cms is an array before calling forEach
                if (cms && Array.isArray(cms)) {
                    cms.forEach((entry: any) => {
                        const url = entry.url.replace(/^\//, '');
                        // ST-3626: nested entries are registered as root routes ahead of the app's own,
                        // so a CMS url such as /blog/foo would shadow the blog module. Refuse those.
                        if (isNestedCategoryUrl(url) && this.isReservedRoutePrefix(url)) {
                            if (!reportedShadowingUrls.has(url)) {
                                reportedShadowingUrls.add(url);
                                console.error(
                                    ...new ExxComError(
                                        528836,
                                        scriptName,
                                        new Error(`Skipping category_page url "${entry.url}": its first segment is an app route`)
                                    ).stamp()
                                );
                            }
                            return;
                        }
                        if (!contains(this.paths, url)) {
                            this.paths.push(url);
                        }
                    });
                } else if (cms) {
                    console.error('Error: Expected array for cms data in setUrls but got:', typeof cms, cms);
                }

                this.paths.push('search');
                this.paths.forEach((path: string) => this.router.config.unshift({ path, component }));
            };

            const cmsData = this.transferState.get(CMS_DATA_STATE_KEY, null);
            const categoriesData = this.transferState.get(CATEGORIES_DATA_STATE_KEY, null);
            if (categoriesData !== null && cmsData !== null) {
                return setUrls(categoriesData, cmsData);
            }

            let cmsRes = await this.cmsService.getAllCategoryPages();

            if (cmsRes.success) cmsRes = cmsRes.data;

            let excRes =
                (await this.apiService.get(['categories/find', `?filters={ "webstore": "${this.environment.siteAbbr}" }`, '&fields=urlComponent'].join(''))) ||
                {};

            if (!isEmpty(cmsRes)) {
                this.transferState.set(CMS_DATA_STATE_KEY, cmsRes);
            }

            if (!excRes.success) {
                throw excRes;
            } else {
                const data = excRes.data;
                if (isEmpty(data)) {
                    return;
                }
                setUrls(data, cmsRes);
                this.transferState.set(CATEGORIES_DATA_STATE_KEY, excRes.data);
            }
        } catch (err) {
            const suppress = ['Unknown Error', 'cannotconnect'];
            if (err && contains(suppress, err.statusText)) {
                return;
            }
            console.error(...new ExxComError(528834, scriptName, err).stamp());
        }
    }

    /**
     * @function loadCategoryPriceCeilings
     * @description Fetches the top-level (type='category') categories that have a
     * price ceiling and populates the module-level ceilings store, keyed by
     * breadcrumb. Called once at app init (see the SPC APP_INITIALIZER). Cached in
     * TransferState so the browser reuses the value rendered on the server.
     */
    async loadCategoryPriceCeilings() {
        try {
            const cached = this.transferState.get(CATEGORY_PRICE_CEILINGS_STATE_KEY, null);
            if (cached !== null) {
                setCategoryPriceCeilings(cached);
                return;
            }
            const res = await this.apiService.get(
                [
                    'categories/find',
                    `?filters={ "webstore": "${this.environment.siteAbbr}", "type": "category" }`,
                    '&fields=breadcrumb,priceCeiling',
                ].join('')
            );
            if (!res || !res.success) {
                return;
            }
            const map = (res.data || []).reduce((acc: Record<string, number>, c: any) => {
                if (c && c.breadcrumb && c.priceCeiling > 0) {
                    acc[c.breadcrumb] = c.priceCeiling;
                }
                return acc;
            }, {});
            setCategoryPriceCeilings(map);
            this.transferState.set(CATEGORY_PRICE_CEILINGS_STATE_KEY, map);
        } catch (err) {
            const suppress = ['Unknown Error', 'cannotconnect'];
            if (err && contains(suppress, err.statusText)) {
                return;
            }
            console.error(...new ExxComError(528835, scriptName, err).stamp());
        }
    }

    get router() {
        return this.routerService.router;
    }

    // CATEGORY RENDERING

    private async initCategory() {
        try {
            this.retrieving$.next(true);
            if (isDevServer()) {
                await this.loadPage();
            } else {
                this.categoryContentStateData = this.transferState.get(CATEGORY_CONTENT_STATE_KEY, {});

                if (!this.isBrowser) {
                    // Loading on the server

                    await this.loadPage();

                    this.transferState.set(CATEGORY_CONTENT_STATE_KEY, this.categoryContentStateData);
                } else {
                    // Loading in the browser
                    if (!isEmpty(this.categoryContentStateData)) {
                        // First browser load
                        const d = this.categoryContentStateData;

                        this.category = new Category({
                            environment: this.environment,
                            location: this.location,
                            routerService: this.routerService,
                            isBrowser: this.isBrowser,
                        });
                        this.category.init(d.params, d.type, d.breadcrumb, d.res);

                        this.transferState.set(CATEGORY_CONTENT_STATE_KEY, {});
                    } else {
                        // Subsequent browser loads
                        await this.loadPage();
                    }
                }
            }

            this.retrieving$.next(false);
        } catch (err) {
            console.error(...new ExxComError(402003, scriptName, err).stamp());
        }
    }

    async loadPage() {
        try {
            if (!this.isBrowser) {
                return;
            }
            this.retrieving$.next(true);
            const url = window.location.toString();
            const { searchParams } = new URL(url);
            const params = {
                page: null,
                sort: null,
                filters: null,
                q: null,
            };
            searchParams.forEach((value, key) => {
                params[key] = value;
            });
            const page = params.page || null;
            const sort = this.parseSort((this.category && this.category.sorting.getParam(params)) || '');
            const filters = this.parseFilters(params.filters);
            let websitePageType = '';
            let breadcrumb: string;
            let type: string;
            let term: string;
            if (params.q) {
                type = 'search';
                term = this.routerService.url.params.q;
            } else {
                const res = await this.getBreadcrumb(this.getUrl());
                websitePageType = get(res, 'websitePageType', '');
                if (!res.success) {
                    this.retrieving$.next(false);
                    return;
                }
                if (res.breadcrumb && websitePageType != 'Tag') {
                    type = 'category';
                    breadcrumb = res.breadcrumb;
                    term = res.breadcrumb;
                } else if (res) {
                    type = 'tag';
                    term = this.getUrl().toLowerCase();
                }
            }

            if (!type || !term) {
                this.retrieving$.next(false);
                return;
            }
            const res = await this.searchSpringService.search(type, term, page, filters, sort);
            res.websitePageType = websitePageType;
            this.categoryContentStateData = { params, type, breadcrumb, res };
            this.category.init(params, type, breadcrumb, res);
            this.retrieving$.next(false);
        } catch (err) {
            console.error(...new ExxComError(320939, scriptName, err).stamp());
            this.retrieving$.next(false);
        }
    }

    // PARSE SORT AND FILTER ON URL

    private parseSort(sortParams: any): string {
        try {
            if (isEmpty(sortParams)) {
                return '';
            }
            const field = Object.keys(sortParams)[0];
            const direction = sortParams[field];
            const sortQueryString = `${field}=${direction}`;
            return sortQueryString;
        } catch (err) {
            console.error(...new ExxComError(320099, scriptName, err).stamp());
        }
    }

    /**
     * @function parseFilters
     * @param {String} filters A string list of the filters from the URL.
     * @description Parses the URL filters, which are generated by category.class
     * filter() when a filter is clicked. A previously generated URL can also be
     * passed directly into the browser address bar. This function parses the URL
     * filters that were generated in category.class, in order to prepare them to
     * be passed into the searchspring.service search function, which concatenates
     * the full URL for the API request.
     */
    private parseFilters(filters: any): string {
        try {
            if (!filters) {
                return '';
            }
            filters = decodeURIComponent(filters);
            filters = filters.split(';');
            let filterQueryString = '';
            filters.forEach((filter: any) => {
                if (!contains(filter, '::')) {
                    return;
                }
                filter = filter.split('::');
                const filterName = filter[0];
                const filterValues = filter[1].split(',').filter(Boolean);
                filterValues.forEach((value: string) => {
                    if (/^(\*?|0?\.?[0-9]{1,6})-(0?\.?[0-9]{1,6}|\*)$/.test(value)) {
                        const range = value.split('-');
                        filterQueryString += `&filter.${filterName}.low=${range[0]}&filter.${filterName}.high=${range[1]}`;
                    } else {
                        filterQueryString += `&filter.${filterName}=${value}`;
                    }
                });
            });
            return filterQueryString;
        } catch (err) {
            console.error(...new ExxComError(429093, scriptName, err).stamp());
        }
    }

    // RETRIEVE BREADCRUMB

    private async getBreadcrumb(urlComponent: string) {
        try {
            const url = [
                'categories/find',
                `?filters={
          "urlComponent": "${urlComponent}",
          "webstore": "${this.environment.siteAbbr}"
        }`,
                '&fields=breadcrumb,websitePageType',
            ].join('');
            const res = await this.apiService.get(url);
            if (!res.success) {
                throw res;
            } else {
                return {
                    success: true,
                    breadcrumb: get(res, 'data[0].breadcrumb'),
                    websitePageType: get(res, 'data[0].websitePageType', ''),
                };
            }
        } catch (err) {
            const suppress = ['Unknown Error', 'cannotconnect'];
            if (err && contains(suppress, err.statusText)) {
                return { success: false };
            }
            console.error(...new ExxComError(623984, scriptName, err).stamp());
        }
    }

    // RETRIEVE CATEGORIES

    /**
     * @function getCategory
     * @param {String} urlComponent - The search parameter
     * @param {String} [fields] - A comma-delimited (no spaces) string (e.g., 'field1,field2')
     * @description Retrieves one category with the given urlComponent from the
     * ExxCom database.
     */
    async getCategory({ urlComponent, fields }: { urlComponent?: string; fields?: string[] } = {}) {
        try {
            if (!urlComponent) {
                urlComponent = this.getUrl();
            }
            if (contains(urlComponent, 'search') || !contains(this.paths, urlComponent)) {
                return null;
            }
            const url = [
                'categories/find',
                `?filters={
          "urlComponent": "${this.getUrl()}",
          "webstore": "${this.environment.siteAbbr}"
        }`,
                '&limit=1',
            ];
            if (!isEmpty(fields)) {
                url.push(`&fields=${fields.join(',')}`);
            }
            const res = await this.apiService.get(url.join(''));
            if (!res.success) {
                throw res;
            }
            return res.data[0];
        } catch (err) {
            console.error(...new ExxComError(792893, scriptName, err).stamp());
        }
    }

    async checkIfCategoryOffboarded(urlComponent: string): Promise<boolean> {
        try {
            const url = urlComponent.startsWith('/') ? urlComponent : `/${urlComponent}`;
            const res: any = await this.apiService.get(`categorypage/check-offboarded?url=${encodeURIComponent(url)}`);
            return res.success && get(res, 'data.offboarded', false);
        } catch (err) {
            console.error(...new ExxComError(792894, scriptName, err).stamp());
            return false;
        }
    }
}
